/**
 * UI-only preferences (kept out of core Settings so the shared Profile type stays untouched).
 * Persisted in localStorage under its own key.
 */
export interface UiSettings {
  /** Colour-blind friendly branch patterns on the mutation meter. */
  colorBlind: boolean;
  /** Skip the opening cinematic on New Evolution. */
  skipIntro: boolean;
  /** Touch control scale multiplier. */
  touchScale: number;
  /** HUD scale multiplier. */
  hudScale: number;
  /** Subtitle text size multiplier. */
  subtitleScale: number;
  /** Player has seen the intro at least once. */
  seenIntro: boolean;
  /** Left-handed touch layout (stick on the right). */
  leftHanded: boolean;
  /** Stage explorer: every form playable from the tree + skip buttons in pause/HUD. */
  explorer: boolean;
}

const KEY = 'evo.ui.v1';

export const DEFAULT_UI_SETTINGS: UiSettings = {
  colorBlind: false,
  skipIntro: false,
  touchScale: 1,
  hudScale: 1,
  subtitleScale: 1,
  seenIntro: false,
  leftHanded: false,
  explorer: true,
};

function storage(): Storage | undefined {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : undefined;
  } catch {
    return undefined;
  }
}

export function loadUiSettings(): UiSettings {
  try {
    const raw = storage()?.getItem(KEY);
    if (!raw) return { ...DEFAULT_UI_SETTINGS };
    return { ...DEFAULT_UI_SETTINGS, ...(JSON.parse(raw) as Partial<UiSettings>) };
  } catch {
    return { ...DEFAULT_UI_SETTINGS };
  }
}

export function saveUiSettings(s: UiSettings): void {
  try {
    storage()?.setItem(KEY, JSON.stringify(s));
  } catch {
    /* ignore quota / privacy mode */
  }
}
